/**
 * angular-bridge.ts
 *
 * THE ONLY FILE that accesses window.__ngInjector.
 * React code must use the typed getters below — never access the injector directly.
 *
 * As each Angular service is replaced by a React hook or Zustand store, remove
 * its getter from this file. When this file has zero Angular/injector references,
 * Phase 6 (Angular removal) can begin.
 */

// Set by main.js immediately after angular.bootstrap() completes.
let _injector: angular.auto.IInjectorService | null = null;

export function setAngularInjector(injector: angular.auto.IInjectorService): void {
  _injector = injector;
  (window as any).__ngInjector = injector;
}

function get<T>(token: string): T {
  if (!_injector) throw new Error(`Angular injector not ready — cannot get ${token}`);
  return _injector.get<T>(token);
}

// ─── Typed service getters ────────────────────────────────────────────────────

/** App config: server base URL, client ID, env settings, etc. */
export function getConfigService() {
  return get<import('../../scripts/services/config/ConfigService').IConfigService>('ConfigService');
}

/** Current user account: email, name, roles, etc. */
export function getAccountService() {
  // AccountService is typed loosely here because its interface is not exported separately.
  return get<any>('AccountService');
}

export function isSuperAdmin(): boolean {
  try { return get<any>('AccountService').IsSuperAdmin() ?? false; } catch { return false; }
}

/** OAuth token storage: access token, refresh, existence checks. */
export function getTokenService() {
  return get<any>('TokenService');
}

/**
 * In-app notification toasts (success, error, info).
 *
 * The underlying Angular service only pushes to an in-memory array; the toast UI re-renders
 * on the next `$digest`. When called from a React/TanStack callback we're outside Angular's
 * digest, so mutating calls are wrapped to schedule one via `$applyAsync` (safe whether or not
 * a digest is already running). Read methods are passed through untouched to avoid digest loops.
 */
export function getNotificationService() {
  type INotificationService = import('../../scripts/services/notification').INotificationService;
  const svc = get<INotificationService>('NotificationService');
  const rootScope = get<angular.IRootScopeService>('$rootScope');
  return new Proxy(svc, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function') return value;
      const name = String(prop);
      if (!/^(add|dismiss)/.test(name)) return value.bind(target);
      return (...args: unknown[]) => {
        const result = (value as (...a: unknown[]) => unknown).apply(target, args);
        rootScope.$applyAsync();
        return result;
      };
    },
  });
}

/** Angular root scope — use only for cross-framework event listening during migration. */
export function getRootScope() {
  return get<angular.IRootScopeService>('$rootScope');
}

/** Current Angular route params (e.g. projectId, collectionId). */
export function getRouteParams(): Record<string, string> {
  return get<Record<string, string>>('$routeParams');
}

/** Large logo URL for the current theme (falls back to the default IIO logo). */
export function getThemeLargeLogo(): string {
  const theme = get<any>('ThemeService').getCurrentTheme();
  return theme?.largeLogo ?? 'https://s3.amazonaws.com/iio-assets/theme/default/iio_logo_large.png';
}


/** Sets the active left-nav item (mirrors NavigationService.setActiveTab). */
export function setNavActiveTab(name: string, subname?: string): void {
  get<any>('NavigationService').setActiveTab(name, subname);
}



/** Sets the Angular ProjectService current project, populating the nav sidebar with project context. */
export function setCurrentProject(project: any): void {
  get<any>('ProjectService').SetCurrentProject(project);
}

/**
 * Clears the Angular CollectionService current collection. Resets the top search
 * bar scope from "This collection" back to "All collections" when returning to a
 * project-level (non-collection) page.
 */
export function clearCurrentCollection(): void {
  get<any>('CollectionService').ClearCurrentCollection();
}

/** Resets grid sort/filter preferences when switching projects (mirrors old ProjectDetailController behaviour). */
export function resetGridPreferencesOnProjectChange(newProjectId: string): void {
  const prefs = get<any>('PreferenceService');
  const root = get<any>('$rootScope');
  if (root.currentProjectId && root.currentProjectId !== newProjectId) {
    prefs.ResetGridPreferences(false);
  }
  root.currentProjectId = newProjectId;
}

/**
 * Broadcasts an Angular $rootScope event from React. Needed while parts of the shell are
 * still Angular (the header task dropdown listens for MonitorableUpdated/Completed).
 * Safe to call before the injector exists — the event is dropped rather than throwing.
 */
export function broadcastAngularEvent(eventName: string, ...args: any[]): void {
  if (!_injector) return;
  get<any>('$rootScope').$broadcast(eventName, ...args);
}

/** Registers a callback for an Angular $rootScope event. Returns an unsubscribe function. */
export function onAngularEvent(eventName: string, callback: (...args: any[]) => void): () => void {
  const rootScope = get<any>('$rootScope');
  const unregister = rootScope.$on(eventName, (event: any, ...args: any[]) => {
    callback(...args);
  });
  return () => unregister();
}



/**
 * Persists the set of collections the Tag Explorer should scope to (mirrors the old
 * ProjectDetailController `_saveSelectedCollections`). The browse page reads this via
 * `CollectionService.FindSelectedOrAll`; an empty set means "all collections".
 */
export function setSelectedCollections(collectionIds: string[]): void {
  get<any>('PreferenceService').SelectedCollections = collectionIds.map(id => ({ id }));
}

/** Sets the Tag Explorer filter to the given tag and navigates to the browse page. */
export function navigateToTagExplorer(projectId: string, tagId: string): void {
  get<any>('PreferenceService').CurrentFilter = {
    size: 1,
    projectId,
    tags: { and: [tagId], or: [], not: [] },
  };
  window.location.hash = `!/project/${projectId}/browse`;
}

/**
 * Broadcasts a BasicSearch query to the Angular search system (only effective on the project detail page).
 * Pass context=0 for Collection scope, context=1 (default) for Project scope (all collections).
 */
export function broadcastBasicSearch(query: string, context: 0 | 1 = 1): void {
  const $rootScope = get<any>('$rootScope');
  $rootScope.$broadcast('SearchContext.Changed.Externally', context);
  $rootScope.$broadcast('BasicSearch.Triggered', query);
}

/** Mirrors TrackService.TrackEvent — fires gtag + console.log for QA click tracking. */
export function trackEvent(category: string, action: string, label?: string): void {
  try { get<any>('TrackService').TrackEvent(category, action, label); } catch {}
}

/** Writes an audit log entry for the given project. Mirrors AuditLogService.Audit. */
export function auditLogEvent(projectId: string, description: string, data: Record<string, unknown>): void {
  try { get<any>('AuditLogService').Audit(projectId, description, data); } catch {}
}

/**
 * Reads the Company/Firm filter selected on the Project List page, persisted via
 * PreferenceService (cookie, ~1yr expiry) so it survives across windows/tabs — the same
 * mechanism used for the browse/search List View vs. Grid View preference.
 */
export function getProjectListOrgFilter(): string | null {
  try { return get<any>('PreferenceService').Get('ProjectListOrgFilter') ?? null; } catch { return null; }
}

/** Persists the Company/Firm filter selected on the Project List page. */
export function setProjectListOrgFilter(orgId: string | null): void {
  try { get<any>('PreferenceService').Set('ProjectListOrgFilter', orgId); } catch {}
}

